import { Standard } from './types';
import { MATH_DOMAINS } from './domains';

// Helper function to get domain ID by name
const getDomainIdByName = (name: string): string => {
  const domain = MATH_DOMAINS.find(d => d.name === name);
  if (!domain) {
    throw new Error(`Domain not found: ${name}`);
  }
  return domain.id;
};

// Create standards with domain IDs
export const mockStandards: Standard[] = [
  {
    id: 'standard-001',
    code: 'K.CC.A.1',
    description: 'Count to 100 by ones and by tens.',
    grade: 'K',
    domainId: getDomainIdByName('Counting and Cardinality'),
    cluster: 'Know number names and the count sequence',
    prerequisites: []
  },
  {
    id: 'standard-002',
    code: 'K.CC.A.2',
    description: 'Count forward beginning from a given number within the known sequence (instead of having to begin at 1).',
    grade: 'K',
    domainId: getDomainIdByName('Counting and Cardinality'),
    cluster: 'Know number names and the count sequence',
    prerequisites: ['standard-001']
  },
  {
    id: 'standard-003',
    code: 'K.CC.A.3',
    description: 'Write numbers from 0 to 20. Represent a number of objects with a written numeral 0-20.',
    grade: 'K',
    domainId: getDomainIdByName('Counting and Cardinality'),
    cluster: 'Know number names and the count sequence',
    prerequisites: ['standard-001', 'standard-002']
  },
  {
    id: 'standard-004',
    code: '1.OA.A.1',
    description: 'Add and subtract within 20 for problems.',
    grade: '1',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Represent and solve problems involving addition and subtraction',
    prerequisites: ['standard-003']
  },
  {
    id: 'standard-005',
    code: '1.OA.A.2',
    description: 'Add three numbers with sum under 20.',
    grade: '1',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Represent and solve problems involving addition and subtraction',
    prerequisites: ['standard-004']
  },
  {
    id: 'standard-006',
    code: '2.OA.A.1',
    description: 'Add and subtract within 100 for problems.',
    grade: '2',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Represent and solve problems involving addition and subtraction',
    prerequisites: ['standard-005']
  },
  {
    id: 'standard-007',
    code: '2.NBT.A.1',
    description: 'Understand place value in three-digit numbers.',
    grade: '2',
    domainId: getDomainIdByName('Number and Operations in Base Ten'),
    cluster: 'Understand place value',
    prerequisites: []
  },
  {
    id: 'standard-008',
    code: '3.OA.A.1',
    description: 'Interpret multiplication as groups of objects.',
    grade: '3',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Represent and solve problems involving multiplication and division',
    prerequisites: ['standard-006']
  },
  {
    id: 'standard-009',
    code: '3.OA.A.2',
    description: 'Understand division with whole numbers.',
    grade: '3',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Represent and solve problems involving multiplication and division',
    prerequisites: ['standard-008']
  },
  {
    id: 'standard-010',
    code: '4.NF.A.1',
    description: 'Understand equivalent fractions using visual models.',
    grade: '4',
    domainId: getDomainIdByName('Number and Operations—Fractions'),
    cluster: 'Extend understanding of fraction equivalence and ordering',
    prerequisites: []
  },
  {
    id: 'standard-011',
    code: '4.NF.A.2',
    description: 'Compare fractions with different denominators.',
    grade: '4',
    domainId: getDomainIdByName('Number and Operations—Fractions'),
    cluster: 'Extend understanding of fraction equivalence and ordering',
    prerequisites: ['standard-010']
  },
  {
    id: 'standard-012',
    code: '5.NBT.A.1',
    description: 'Understand place value relationships in numbers.',
    grade: '5',
    domainId: getDomainIdByName('Number and Operations in Base Ten'),
    cluster: 'Understand the place value system',
    prerequisites: ['standard-007']
  },
  {
    id: 'standard-013',
    code: '5.NBT.A.2',
    description: 'Explain patterns when multiplying by powers 10.',
    grade: '5',
    domainId: getDomainIdByName('Number and Operations in Base Ten'),
    cluster: 'Understand the place value system',
    prerequisites: ['standard-012']
  },
  {
    id: 'standard-014',
    code: '6.RP.A.1',
    description: 'Understand ratios between two quantities.',
    grade: '6',
    domainId: getDomainIdByName('Ratios and Proportional Relationships'),
    cluster: 'Understand ratio concepts and use ratio reasoning to solve problems',
    prerequisites: []
  },
  {
    id: 'standard-015',
    code: '6.RP.A.2',
    description: 'Understand unit rates in ratio relationships.',
    grade: '6',
    domainId: getDomainIdByName('Ratios and Proportional Relationships'),
    cluster: 'Understand ratio concepts and use ratio reasoning to solve problems',
    prerequisites: ['standard-014']
  },
  {
    id: 'standard-016',
    code: '6.RP.A.3',
    description: 'Use ratio and rate reasoning to solve real-world and mathematical problems.',
    grade: '6',
    domainId: getDomainIdByName('Ratios and Proportional Relationships'),
    cluster: 'Understand ratio concepts and use ratio reasoning to solve problems',
    prerequisites: ['standard-015']
  },
  {
    id: 'standard-017',
    code: '1.NBT.A.1',
    description: 'Count to 120, starting at any number less than 120. In this range, read and write numerals and represent a number of objects with a written numeral.',
    grade: '1',
    domainId: getDomainIdByName('Number and Operations in Base Ten'),
    cluster: 'Extend the counting sequence',
    prerequisites: ['standard-003']
  },
  {
    id: 'standard-018',
    code: '3.NF.A.1',
    description: 'Understand a fraction 1/b as the quantity formed by 1 part when a whole is partitioned into b equal parts; understand a fraction a/b as the quantity formed by a parts of size 1/b.',
    grade: '3',
    domainId: getDomainIdByName('Number and Operations—Fractions'),
    cluster: 'Develop understanding of fractions as numbers',
    prerequisites: []
  },
  {
    id: 'standard-019',
    code: '3.NF.A.2',
    description: 'Understand a fraction as a number on the number line; represent fractions on a number line diagram.',
    grade: '3',
    domainId: getDomainIdByName('Number and Operations—Fractions'),
    cluster: 'Develop understanding of fractions as numbers',
    prerequisites: ['standard-018']
  },
  {
    id: 'standard-020',
    code: '3.NF.A.3',
    description: 'Explain equivalence of fractions in special cases, and compare fractions by reasoning about their size.',
    grade: '3',
    domainId: getDomainIdByName('Number and Operations—Fractions'),
    cluster: 'Develop understanding of fractions as numbers',
    prerequisites: ['standard-019']
  },
  {
    id: 'standard-021',
    code: '2.MD.D.9',
    description: 'Generate measurement data by measuring lengths of several objects to the nearest whole unit, or by making repeated measurements of the same object. Show the measurements by making a line plot, where the horizontal scale is marked off in whole-number units.',
    grade: '2',
    domainId: getDomainIdByName('Measurement and Data'),
    cluster: 'Represent and interpret data',
    prerequisites: []
  },
  {
    id: 'standard-022',
    code: '2.MD.D.10',
    description: 'Draw a picture graph and a bar graph (with single-unit scale) to represent a data set with up to four categories. Solve simple put-together, take-apart, and compare problems using information presented in a bar graph.',
    grade: '2',
    domainId: getDomainIdByName('Measurement and Data'),
    cluster: 'Represent and interpret data',
    prerequisites: ['standard-021']
  },
  {
    id: 'standard-023',
    code: '3.OA.B.5',
    description: 'Apply properties of operations as strategies to multiply and divide.',
    grade: '3',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Understand properties of multiplication and the relationship between multiplication and division',
    prerequisites: ['standard-008']
  },
  {
    id: 'standard-024',
    code: '3.OA.C.7',
    description: 'Fluently multiply and divide within 100, using strategies such as the relationship between multiplication and division or properties of operations.',
    grade: '3',
    domainId: getDomainIdByName('Operations and Algebraic Thinking'),
    cluster: 'Multiply and divide within 100',
    prerequisites: ['standard-023']
  },
  {
    id: 'standard-025',
    code: '2.MD.C.7',
    description: 'Tell and write time from analog and digital clocks to the nearest five minutes, using a.m. and p.m.',
    grade: '2',
    domainId: getDomainIdByName('Measurement and Data'),
    cluster: 'Work with time and money',
    prerequisites: []
  },
  {
    id: 'standard-026',
    code: '2.MD.C.8',
    description: 'Solve word problems involving dollar bills, quarters, dimes, nickels, and pennies, using $ and ¢ symbols appropriately.',
    grade: '2',
    domainId: getDomainIdByName('Measurement and Data'),
    cluster: 'Work with time and money',
    prerequisites: ['standard-025']
  }
]; 